/*
  THAANI — Produkt-Slider
  Vanilla JS, kein Framework. Steuert die Pfeil-Buttons einer nativen
  Scroll-Snap-Spur und haelt sie mit dem Scrollzustand synchron.
  Gescrollt wird immer um genau eine Kartenbreite.
*/

class ThaaniProduktSlider extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.thaani-ps__scroller');
    this.items = Array.from(this.querySelectorAll('.thaani-ps__item'));

    if (!this.scroller || this.items.length === 0) return;

    this.prevButton = this.querySelector('.thaani-ps__arrow--prev');
    this.nextButton = this.querySelector('.thaani-ps__arrow--next');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onScroll = this.handleScroll.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onResize = this.handleResize.bind(this);
    this.frame = null;

    this.bindEvents();
    this.classList.add('is-ready');
    this.update();
  }

  disconnectedCallback() {
    if (this.scroller) {
      this.scroller.removeEventListener('scroll', this.onScroll);
      this.scroller.removeEventListener('keydown', this.onKeyDown);
    }

    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.frame) window.cancelAnimationFrame(this.frame);
  }

  bindEvents() {
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    this.scroller.addEventListener('keydown', this.onKeyDown);

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.step(-1));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.step(1));
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.scroller);
    } else {
      window.addEventListener('resize', this.onResize);
    }
  }

  handleScroll() {
    if (this.frame) window.cancelAnimationFrame(this.frame);

    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  handleResize() {
    this.update();
  }

  handleKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.step(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.scrollToPosition(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.scrollToPosition(this.maxScroll());
    }
  }

  /* Abstand zwischen zwei Kartenanfaengen — inklusive Luecke */
  stepSize() {
    if (this.items.length > 1) {
      const distance = this.items[1].offsetLeft - this.items[0].offsetLeft;
      if (distance > 0) return distance;
    }

    return this.items[0].offsetWidth || this.scroller.clientWidth;
  }

  maxScroll() {
    return Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
  }

  step(direction) {
    const target = this.scroller.scrollLeft + this.stepSize() * direction;
    this.scrollToPosition(Math.min(Math.max(target, 0), this.maxScroll()));
  }

  scrollToPosition(left) {
    this.scroller.scrollTo({
      left: left,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  update() {
    const max = this.maxScroll();
    const position = this.scroller.scrollLeft;

    // Ohne Ueberlauf braucht es keine Pfeile
    this.classList.toggle('is-static', max <= 1);

    this.setDisabled(this.prevButton, position <= 1);
    this.setDisabled(this.nextButton, position >= max - 1);
  }

  setDisabled(button, isDisabled) {
    if (!button) return;

    // aria-disabled statt disabled: der Fokus bleibt am Ende der Spur erhalten
    button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
  }
}

if (!customElements.get('thaani-produkt-slider')) {
  customElements.define('thaani-produkt-slider', ThaaniProduktSlider);
}
